import { useEffect, useRef, useState } from 'react';
import { globals } from '../utils';

// Type for the event payload
export interface WebviewEventPayload {
  [key: string]: any;
}
// Type for the event info structure
export interface WebviewEvent {
  type: string;
  payload: WebviewEventPayload;
}
// Extend Window type to include ReactNativeWebView
declare global {
  interface Window {
    ReactNativeWebView?: {
      postMessage?: (message: string) => void;
    };
  }
}

/**
 * Hook to communicate with ReactNativeWebView from Next.js
 * Returns sendEvent function and isWebviewAvailable flag
 */
export function useReactWebviewCommunicator(): {
  sendEvent: (type: string, payload: WebviewEventPayload) => void;
  clickEvent: (payload: WebviewEventPayload) => void;
  isWebviewAvailable: boolean;
} {
  const [isWebviewAvailable, setIsWebviewAvailable] = useState(false);
  const webviewRef = useRef<Window['ReactNativeWebView'] | null>(null);

  const isNativeClickEnabled = globals?.releaseFlags?.REACT_NATIVE_COOKIE_CLICK;

  useEffect(() => {
    // Check if ReactNativeWebView is available on the client side
    if (typeof window !== 'undefined' && !!window.ReactNativeWebView && isNativeClickEnabled) {
      webviewRef.current = window.ReactNativeWebView;
      setIsWebviewAvailable(true);
    }
  }, [isNativeClickEnabled]); // Re-run when isNativeClickEnabled changes

  /**
   * Send a custom event to ReactNativeWebView if available
   * @param type - Type of the event (e.g., 'CLICK_EVENT')
   * @param payload - Data to send (e.g., { tag, id, className, text })
   */
  function sendEvent(type: string, payload: WebviewEventPayload = {}) {
    const info: WebviewEvent = { type, payload };
    if (webviewRef.current && typeof webviewRef.current.postMessage === 'function') {
      webviewRef.current.postMessage(JSON.stringify(info));
      // Log the posted message
      if (typeof window !== 'undefined' && window.console) {
        // eslint-disable-next-line no-console
        console.log('[ReactNativeWebView] Message posted:', info);
      }
    } else {
      // eslint-disable-next-line no-console
      console.log('Unable to post a message [ReactNativeWebView]:', info);
    }
  }

  /**
   * Send a click event to ReactNativeWebView if available
   * @param payload - Data to send (e.g., { tag, id, className, text })
   */
  function clickEvent(payload: WebviewEventPayload = {}) {
    sendEvent('CLICK_EVENT', payload);
  }

  return { sendEvent, clickEvent, isWebviewAvailable };
}
